import ClickEventLogger from '@/components/common/ClickEventLogger';
import LinkTag from '@/components/common/link-tag';
import HospitalListItem from '@/components/hospital/hospital-list-item/HospitalListItem';
import ReportSelectModal from '@/components/molecules/modal/select-modal/report-select-modal';
import {
  EmptyResult,
  RequestInfoBox,
  ResultSection,
  SearchMagazineItem,
  SearchQuestionItem,
} from '@/components/templates/mobile/home/home-search-template/components';
import Doctor from '@/components/templates/mobile/home/home-search-template/components/search-result/doctor/Doctor';
import DiseaseEncyclopedia from '@/entities/disease-encyclopedia/ui/DiseaseEncyclopedia';
import MedicineDictionary from '@/entities/medicine-dictionary/ui/MedicineDictionary';
import { SearchDoctorViewModel } from '@/hooks/search/doctor/useSearchDoctorViewModel';
import { HomeSearchViewModel } from '@/hooks/search/useHomeSearchViewModel';
import useReportModal from '@/hooks/useReportModal';
import theme from '@/styles/theme';
import { ROUTES } from '@/types/common';
import { ContractType } from '@/types/hospital';
import { Magazine } from '@/types/magazine';
import { EIntegratedSearchTabType, ERequestButtonType } from '@/types/search';
import React from 'react';
import FeatureByKeyword from '../component/FeatureByKeyword';
import IntegratedSearchResultSkeleton from '../component/IntegratedSearchResultSkeleton';
import SnippetByKeyword from '../component/SnippetByKeyword';

const getSearchResultTemplateItemStyle = (isLast: boolean) => {
  return {
    margin: '24px 24px 0px',
    padding: '0px 0px 24px',
    borderBottom: isLast ? 'none' : `1px solid ${theme.color.gray40}`,
  };
};

const IntegratedSearchResult = ({
  viewModel,
  doctorViewModel,
}: {
  viewModel: HomeSearchViewModel;
  doctorViewModel: SearchDoctorViewModel;
}) => {
  const { showReportModal, onCloseReportModal, reportItems } = useReportModal();

  if (viewModel.isIntegratedSearchLoading) {
    return <IntegratedSearchResultSkeleton />;
  }

  if (viewModel.hasNoData) {
    return <EmptyResult handleClickRequestButton={viewModel.handleClickRequestButton} />;
  }

  return (
    <>
      <SnippetByKeyword
        keyword={viewModel.generalSearchMetaByKeyword?.keyword ?? ''}
        snippet={viewModel.generalSearchMetaByKeyword?.snippet}
        navigateToDoctorList={viewModel.navigateToDoctorList}
      />
      <FeatureByKeyword
        features={viewModel.generalSearchMetaByKeyword?.features}
        navigateToFindPharmacyScreen={viewModel.navigateToFindPharmacyScreen}
        navigateDietInjectionScreen={viewModel.navigateDietInjectionScreen}
        navigateToEmergencyContraceptiveScreen={viewModel.navigateToEmergencyContraceptiveScreen}
      />
      <ResultSection title='비대면 진료' hasResultData={!!viewModel.doctors.length}>
        <ResultSection.Result>
          {viewModel.doctors?.map((doctor) => (
            <Doctor
              onClick={() => doctorViewModel.onClickDoctor(doctor.publicId)}
              key={`search-result-doctor-${doctor.id}`}
              doctor={doctor}
            />
          ))}
        </ResultSection.Result>
        {viewModel.hasMoreDoctors && (
          <ResultSection.MoreButton onClick={() => viewModel.onChangeTabByMoreData(EIntegratedSearchTabType.Doctor)} />
        )}
      </ResultSection>
      <ResultSection title={'가까운 병원 찾기'} hasResultData={!!viewModel.keywordSearchHospital?.elements.length}>
        <ResultSection.ResultAddress
          address={viewModel.getHeaderAddress()}
          getCurrentLocation={viewModel.requestGeolocationPermission}
        />
        <ResultSection.Result>
          {viewModel.keywordSearchHospital?.elements.map((hospital) => (
            <LinkTag
              key={`search_hospital_${hospital.hospitalPublicId}`}
              onClickNavigateToScreen={() => viewModel.goToAppHospital(hospital.hospitalPublicId)}
              href={`${ROUTES.HOSPITAL.DETAIL.path}${hospital.hospitalPublicId}`}
            >
              <ClickEventLogger
                pageName={'user_web_search'}
                eventName='search_item_click'
                payload={{
                  keyword: viewModel.keyword,
                  hospital_id: hospital.hospitalPublicId,
                  tab: EIntegratedSearchTabType.All,
                  result_section: EIntegratedSearchTabType.Hospital,
                }}
              >
                <HospitalListItem
                  name={hospital.hospitalName}
                  distance={hospital.hospitalDistance}
                  rating={hospital.reviewScore}
                  reviewTotalCount={hospital.reviewTotalCount}
                  badges={hospital.hospitalBadges}
                  imageUrl={hospital.hospitalRepresentativeImageUrl}
                  address={hospital.hospitalAddress}
                  status={hospital.hospitalOperationStatus}
                  isBookable={hospital.isBookable}
                  representativeDepartmentName={hospital.hospitalRepresentativeDepartmentName}
                  hospitalPid={hospital.hospitalPublicId}
                  doctorDetailPageHref={`${ROUTES.HOSPITAL.DETAIL.path}${hospital.hospitalPublicId}`}
                  onClickMoveToDoctorDetail={() => {
                    viewModel.goToAppHospital(hospital.hospitalPublicId);
                  }}
                  hasNonF2fDoctors={
                    !!hospital.doctors.find((doctor) => doctor.contractType !== ContractType.FaceToFace)
                  }
                />
              </ClickEventLogger>
            </LinkTag>
          ))}
        </ResultSection.Result>
        {viewModel.keywordSearchHospital?.hasMore && (
          <ResultSection.MoreButton
            onClick={() => viewModel.onChangeTabByMoreData(EIntegratedSearchTabType.Hospital)}
          />
        )}
      </ResultSection>
      <ResultSection title={'실시간 의료상담'} hasResultData={!!viewModel.keywordSearchQna?.elements.length}>
        <ResultSection.Result>
          {viewModel.keywordSearchQna?.elements.map((question) => {
            return (
              <SearchQuestionItem
                key={question.question.questionPid}
                questionCard={question}
                onClickQuestion={viewModel.handleClickQuestion}
              />
            );
          })}
        </ResultSection.Result>
        {viewModel.keywordSearchQna?.hasMore && (
          <ResultSection.MoreButton onClick={() => viewModel.onChangeTabByMoreData(EIntegratedSearchTabType.Qna)} />
        )}
      </ResultSection>
      <ResultSection title={'건강 매거진'} hasResultData={!!viewModel.keywordSearchMagazine?.elements.length}>
        <ResultSection.Result>
          {viewModel.keywordSearchMagazine?.elements.map((magazine, index) => (
            <SearchMagazineItem
              key={magazine.magazinePid}
              magazine={magazine}
              onClickMagazine={() => viewModel.handleClickMagazine(magazine as Magazine)}
              pageName={'mobile_web_search'}
              containerStyle={getSearchResultTemplateItemStyle(
                viewModel.keywordSearchMagazine ? index === viewModel.keywordSearchMagazine?.elements.length - 1 : false
              )}
            />
          ))}
        </ResultSection.Result>
        {viewModel.keywordSearchMagazine?.hasMore && (
          <ResultSection.MoreButton
            onClick={() => viewModel.onChangeTabByMoreData(EIntegratedSearchTabType.Magazine)}
          />
        )}
      </ResultSection>
      <ResultSection title={'의약품 사전'} hasResultData={!!viewModel.keywordSearchMedicineDictionary?.elements.length}>
        <ResultSection.Result>
          {viewModel.keywordSearchMedicineDictionary?.elements.map((medicineDictionary, index, array) => (
            <React.Fragment key={medicineDictionary.getPublicId()}>
              <div key={medicineDictionary.getPublicId()} className='flex flex-col gap-[12px] p-[24px]'>
                <LinkTag
                  href={`${ROUTES.MEDICINE_DICTIONARY.DETAIL.path}/${medicineDictionary.getPublicId()}`}
                  onClickNavigateToScreen={viewModel.handleClickMedicineDictionary(medicineDictionary)}
                >
                  <MedicineDictionary
                    title={medicineDictionary.getBasicInfoName()}
                    description={medicineDictionary.getPharmaceuticalEffectLiteracy()}
                    summary={medicineDictionary.getWholeSummary()}
                    image={medicineDictionary.getRepresentativeImage()}
                    imageAlt={medicineDictionary.getSimplifiedName()}
                  />
                </LinkTag>
              </div>
              {index !== array.length - 1 && <div className='h-[1px] w-full bg-gray-40' />}
            </React.Fragment>
          ))}
        </ResultSection.Result>

        {viewModel.keywordSearchMedicineDictionary?.hasMore && (
          <ResultSection.MoreButton
            onClick={() => viewModel.onChangeTabByMoreData(EIntegratedSearchTabType.MedicineDictionary)}
          />
        )}
      </ResultSection>
      <ResultSection title={'질환백과'} hasResultData={!!viewModel.keywordSearchDiseaseEncyclopedia?.elements.length}>
        <ResultSection.Result>
          {viewModel.keywordSearchDiseaseEncyclopedia?.elements.map((diseaseEncyclopedia, index, array) => (
            <React.Fragment key={diseaseEncyclopedia.getPublicId()}>
              <div key={diseaseEncyclopedia.getPublicId()} className='flex flex-col gap-[12px] p-[24px]'>
                <LinkTag
                  href={`${ROUTES.DISEASE_ENCYCLOPEDIA.DETAIL.path}/${diseaseEncyclopedia.getPublicId()}`}
                  onClickNavigateToScreen={viewModel.handleClickDiseaseEncyclopedia(diseaseEncyclopedia)}
                >
                  <DiseaseEncyclopedia
                    title={diseaseEncyclopedia.getTitle()}
                    summary={diseaseEncyclopedia.getContent()}
                    image={diseaseEncyclopedia.getTitleImageUrl() ?? undefined}
                    imageAlt={diseaseEncyclopedia.getTitle()}
                  />
                </LinkTag>
              </div>
              {index !== array.length - 1 && <div className='h-[1px] w-full bg-gray-40' />}
            </React.Fragment>
          ))}
        </ResultSection.Result>
        {viewModel.keywordSearchDiseaseEncyclopedia?.hasMore && (
          <ResultSection.MoreButton
            onClick={() => viewModel.onChangeTabByMoreData(EIntegratedSearchTabType.DiseaseEncyclopedia)}
          />
        )}
      </ResultSection>
      <RequestInfoBox handleClickRequestButton={() => viewModel.handleClickRequestButton(ERequestButtonType.BOTTOM)} />
      {reportItems && (
        <ReportSelectModal isVisible={showReportModal} onClose={onCloseReportModal} selectList={reportItems} />
      )}
    </>
  );
};

export default IntegratedSearchResult;
